import type {
  MetadataMetadataFieldValueSchema,
  MetadataMetadataValuesSchema,
  MetadataMetadataValuesSchemaNullable,
} from '@iconik-io/iconik-api/src/services/metadata';
import type { SearchCriteriaRangeFilterSchema } from '@iconik-io/iconik-api/src/services/search';

/**
 * This module contains some adjusted types for the metadata field related schemas.
 * Due to some API library limitations some fields are not possible to type properly
 * in schemas directly on the API side.
 */

export const AI_GENERATED = 'AI';
export const AI_GENERATED_ACCEPTED = 'AI_ACCEPTED';
export const AI_GENERATED_EDITED = 'AI_EDITED';

export type AiGenerated =
  | typeof AI_GENERATED
  | typeof AI_GENERATED_ACCEPTED
  | typeof AI_GENERATED_EDITED;

export type MetadataFieldValue = {
  value: string;
  label?: string;
  ai_generated?: AiGenerated;
};

export type MetadataMetadataFieldValueSchemaPatched = Omit<
  MetadataMetadataFieldValueSchema,
  'field_values'
> & {
  field_values?: MetadataFieldValue[];
};

export type MetadataFieldValuesPatched = {
  [fieldName: string]: MetadataMetadataFieldValueSchemaPatched;
};

export type MetadataMetadataValuesSchemaPatched = Omit<
  MetadataMetadataValuesSchema,
  'metadata_values'
> & {
  metadata_values: MetadataFieldValuesPatched;
};

export type GcviTag = {
  value: string;
  score?: number;
};

export type ModeType = MetadataMetadataFieldValueSchemaPatched['mode'];

export type MetadataValue =
  | null
  | undefined
  | string
  | boolean
  | number
  | ModeType
  | (string | MetadataFieldValue)[]
  | SearchCriteriaRangeFilterSchema;

export type MetadataValues = {
  _gcvi_tags?: GcviTag[];
  [key: string]: MetadataValue;
};

/**
 * The generated response declares the loose field values, so the patched shape
 * is stated once here rather than at each caller. Both the plain and nullable
 * responses are accepted, because the endpoints return either and every reader
 * wants the same field values out.
 */
export const asMetadataValuesResponse = (
  body: MetadataMetadataValuesSchema | MetadataMetadataValuesSchemaNullable,
): MetadataMetadataValuesSchemaPatched =>
  // biome-ignore lint/plugin: see above.
  body as MetadataMetadataValuesSchemaPatched;

/** The field values of such a response, read on their own. */
export const asFieldValues = (
  values:
    | MetadataMetadataValuesSchema['metadata_values']
    | MetadataMetadataValuesSchemaNullable['metadata_values'],
): MetadataFieldValuesPatched =>
  // biome-ignore lint/plugin: see asMetadataValuesResponse.
  values as MetadataFieldValuesPatched;
